import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useGLTF, Billboard, Text } from '@react-three/drei';
import AnimatedResident from './AnimatedResident';
import SafeMount from './SafeMount';
import { ROOMS } from '../rooms';
import { useWorldStore } from '../state/worldStore';
import { useCityStore } from '../city/cityStore';
import { stateAt } from '../city/cityMotion';
import { serverNow } from '../services/worldSocket';
import { listWorldObjects } from '../services/worldObjects';
import { talkMove } from '../city/bodyLanguage';

// RoomStory — City Stories INSIDE a venue (Khari, 2026-10-29: "yes, please add
// indoor scenes", after an episode narrated the Corner Market from the pavement).
//
// When an artist's episode has a scene inside this room, the server's timeline
// puts them here (a stay with `r` = this room, spot `in0` for the lead, `in1`
// for a co-star): the street stops drawing them at the door (CityResident) and
// this draws them at the room's STORY MARKS, in their own body, lit like
// everyone else in here. The voices are RoomStoryAudio's.
//
// ⚠ AND THEY EMOTE (2026-11-04, Khari: "Story scenes inside rooms: add"): their
// hands while their line plays and listening while the other one speaks, from the
// story's own line schedule (city/bodyLanguage talkMove, as on the street).
//
// THE MARKS: placed in the World (⚙ → Placeables → "Story marks": #1 the lead,
// #2 a co-star; saved in ROOM space, services/roomSpace), else rooms.js
// `storyMarks` (the Corner Market's first guess). Only #1 placed: the co-star
// stands beside the lead. While the Placeables tool is open the marks show as
// numbered ghosts, here in the room's own space, where the bodies will stand.
//
// ⚠ ONE ARTIST, ONE PLACE: the same shared timeline decides both sides, so a
// viewer on the street sees them go in, and a viewer in here sees them arrive.
//
// Mounted beside InteriorResidents (inside the room group, outside its scale
// group): a person is sized against the world, not the building's fit.
const BESIDE = 0.9;   // a co-star with no mark of their own stands this far beside the lead

function marksFrom(rows, fallback) {
  const sorted = [...(rows || [])].sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
  if (!sorted.length) return fallback || null;
  const m0 = { pos: sorted[0].pos, rotY: sorted[0].rotY || 0 };
  const m1 = sorted[1]
    ? { pos: sorted[1].pos, rotY: sorted[1].rotY || 0 }
    : { pos: [m0.pos[0] + Math.cos(m0.rotY) * BESIDE, m0.pos[1] || 0, m0.pos[2] - Math.sin(m0.rotY) * BESIDE], rotY: m0.rotY };
  return { in0: m0, in1: m1 };
}

function MarkGhost({ mark, n }) {
  return (
    <group position={mark.pos} rotation={[0, mark.rotY || 0, 0]}>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.006, 0]}>
        <ringGeometry args={[0.16, 0.2, 40]} />
        <meshBasicMaterial color="#ff8a9c" transparent opacity={0.85} depthTest={false} side={2} />
      </mesh>
      {/* Which way they will face: local +z, the stored rotY's convention. */}
      <mesh position={[0, 0.006, 0.32]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.09, 0.14, 3]} />
        <meshBasicMaterial color="#ff8a9c" transparent opacity={0.85} depthTest={false} />
      </mesh>
      <Billboard position={[0, 0.95, 0]}>
        <Text fontSize={0.12} color="#ffd2d9" anchorX="center" anchorY="middle" renderOrder={10}>
          {n === 1 ? '#1 lead' : '#2 co-star'}
        </Text>
      </Billboard>
    </group>
  );
}

export default function RoomStory({ roomId }) {
  const editing = useWorldStore((s) => s.adminTool) === 'placeables';
  const liveRows = useWorldStore((s) => s.worldObjects);
  const live = useCityStore((s) => s.live);
  const residents = useCityStore((s) => s.snap?.residents || null);
  const speaking = useCityStore((s) => s.speaking);
  const npcList = useWorldStore((s) => s.npcList);
  const [placed, setPlaced] = useState(null);
  const [now, setNow] = useState(() => serverNow());

  // The saved marks (re-read when the tool closes, so a fresh placement counts).
  useEffect(() => {
    if (!roomId || editing) return undefined;
    let dead = false;
    listWorldObjects(roomId, 'story_mark').then((rows) => { if (!dead) setPlaced(rows || []); }).catch(() => {});
    return () => { dead = true; };
  }, [roomId, editing]);
  useEffect(() => {
    const t = setInterval(() => setNow(serverNow()), 500);
    return () => clearInterval(t);
  }, []);
  const bodies = useMemo(() => {
    const m = new Map();
    for (const n of npcList || []) if (n?.avatarId != null && n.rigged && !n.test) m.set(Number(n.avatarId), n);
    return m;
  }, [npcList]);

  const rows = editing ? (liveRows || []).filter((o) => o.zone_id === roomId && o.kind === 'story_mark') : placed;
  const marks = marksFrom(rows, ROOMS[roomId]?.storyMarks || null);
  if (!marks) return null;

  const here = [];
  if (live && residents?.length) {
    const cs = useCityStore.getState();
    for (const r of residents) {
      const st = stateAt(cs.byId[r.avatarId] || r, now, cs.convs);
      if (st?.inside === roomId && marks[st.spot]) {
        const story = st.story != null ? cs.stories?.[st.story] : null;
        const e = Number.isFinite(cs.byId[r.avatarId]?.e) ? cs.byId[r.avatarId].e : 50;
        const move = story ? talkMove({ lines: story.lines || [] }, r.avatarId, now, e) : null;
        here.push({ r, mark: marks[st.spot], move });
      }
    }
  }
  const roomLight = (ROOMS[roomId]?.light ?? 1) * (ROOMS[roomId]?.model ? 0.45 : 1);

  return (
    <group>
      {editing && <MarkGhost mark={marks.in0} n={1} />}
      {editing && <MarkGhost mark={marks.in1} n={2} />}
      {here.map(({ r, mark, move }) => {
        const body = bodies.get(Number(r.avatarId)) || null;
        const height = body?.height || 0.75;
        return (
          <group key={r.avatarId} position={mark.pos} rotation={[0, mark.rotY || 0, 0]}>
            {body ? (
              <SafeMount label={`story body ${r.name || r.avatarId}`} retryMs={30000} onRetry={() => useGLTF.clear([body.rigged, body.walk, body.idle].filter(Boolean))}>
                <Suspense fallback={null}>
                  <AnimatedResident
                    meshUrl={body.rigged}
                    rigPose={body.pose || null}
                    clipMap={body.clips || null}
                    clipUrls={[body.walk, body.idle].filter(Boolean)}
                    play="idle"
                    activity={move}
                    activityTalks={!!move}
                    avatarId={r.avatarId}
                    position={[0, 0, 0]}
                    rotationY={0}
                    height={height}
                    speaking={(speaking?.[r.avatarId] || 0) > now}
                    indoor
                    lightScale={roomLight}
                  />
                </Suspense>
              </SafeMount>
            ) : (
              // A stand-in at their height until the body arrives.
              <mesh position={[0, height * 0.5, 0]}>
                <capsuleGeometry args={[height * 0.13, height * 0.62, 4, 8]} />
                <meshStandardMaterial color="#2e2a3a" roughness={0.8} metalness={0} />
              </mesh>
            )}
          </group>
        );
      })}
    </group>
  );
}
